import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { GroupVisibility, IsoDateTime, UserRef } from '@/shared/api/types';

/*
 * Calendar API (backend module `calendar`, route root /api/calendar — docs/modules/calendar.md).
 * Query keys start with the module id `calendar`.
 */

export type CalendarKind = 'personal' | 'group' | 'company';
export type EventPrivacy = 'public' | 'private';
export type EventAvailability = 'busy' | 'tentative' | 'free' | 'away';
export type AttendeeResponse = 'pending' | 'accepted' | 'declined' | 'tentative';
export type EditScope = 'all' | 'this' | 'following';
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type WeekDayCode = 'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su';
export type CalendarViewName = 'month' | 'week' | 'day' | 'agenda';

/** ISO date without time (`2026-10-09`). */
export type IsoDate = string;

export interface CalendarGroupRef {
  id: string;
  name: string;
  avatarUrl: string | null;
  isProject: boolean;
  visibility: GroupVisibility;
}

export interface CalendarItem {
  id: string;
  kind: CalendarKind;
  name: string | null;
  color: string;
  defaultColor: string;
  isVisible: boolean;
  isFollowed: boolean;
  isMine: boolean;
  owner: UserRef | null;
  group: CalendarGroupRef | null;
  canCreate: boolean;
  canManage: boolean;
}

export interface Room {
  id: string;
  name: string;
  location: string | null;
  capacity: number;
  description: string | null;
  equipment: string[];
  color: string;
  defaultColor: string;
  isVisible: boolean;
}

export interface CalendarSettings {
  workDayStart: string;
  workDayEnd: string;
  workDays: number[];
  weekStart: number;
  defaultView: CalendarViewName;
  showDeclined: boolean;
  defaultReminders: number[];
}

export interface CalendarList {
  calendars: CalendarItem[];
  rooms: Room[];
  settings: CalendarSettings;
  pendingInvitations: number;
}

export interface Recurrence {
  frequency: RecurrenceFrequency;
  interval: number;
  weekDays: WeekDayCode[];
  monthlyByWeekday: boolean;
  until: IsoDate | null;
  count: number | null;
}

export interface Occurrence {
  key: string;
  eventId: string;
  seriesId: string | null;
  originalStart: IsoDateTime | null;
  start: IsoDateTime;
  end: IsoDateTime;
  isAllDay: boolean;
  title: string | null;
  location: string | null;
  calendarId: string;
  roomId: string | null;
  roomName: string | null;
  color: string | null;
  isRecurring: boolean;
  isPrivate: boolean;
  isBusyOnly: boolean;
  availability: EventAvailability;
  myResponse: AttendeeResponse | null;
  attendeeCount: number;
  organizerId: string | null;
  canEdit: boolean;
  sources: string[];
  /** A linked event mirrors a record of this module (`crm`): read-only in the calendar. */
  linkedModule?: string | null;
}

/** The record of another module a linked event mirrors (a CRM meeting). */
export interface LinkedSource {
  module: string;
  type: string;
  id: string;
  /** SPA deep link of the record. */
  link: string | null;
  /** What the link opens (e.g. a deal's title). */
  title: string | null;
  isCompleted: boolean;
}

export interface OccurrenceRange {
  items: Occurrence[];
  truncated: boolean;
}

export interface Attendee {
  user: UserRef;
  response: AttendeeResponse;
  isOrganizer: boolean;
  respondedAt: IsoDateTime | null;
}

export interface EventCalendarRef {
  id: string;
  kind: CalendarKind;
  name: string | null;
  color: string;
  owner: UserRef | null;
  group: CalendarGroupRef | null;
}

export interface RoomRef {
  id: string;
  name: string;
  location: string | null;
  capacity: number;
  isDeleted: boolean;
}

export interface EventDetails {
  id: string;
  key: string;
  seriesId: string | null;
  originalStart: IsoDateTime | null;
  occurrence: IsoDateTime | null;
  calendar: EventCalendarRef;
  title: string;
  descriptionHtml: string;
  description: string | null;
  location: string | null;
  room: RoomRef | null;
  start: IsoDateTime;
  end: IsoDateTime;
  isAllDay: boolean;
  timeZone: string;
  seriesStart: IsoDateTime;
  isRecurring: boolean;
  recurrence: Recurrence | null;
  recurrenceRule: string | null;
  privacy: EventPrivacy;
  availability: EventAvailability;
  color: string | null;
  organizer: UserRef;
  attendees: Attendee[];
  counts: { accepted: number; declined: number; tentative: number; pending: number };
  myResponse: AttendeeResponse | null;
  myReminders: number[];
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canRespond: boolean;
    canJoin: boolean;
    canSetReminders: boolean;
  };
  version: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  /** Set for a linked event (a CRM meeting): it is changed where it comes from. */
  linked?: LinkedSource | null;
}

export interface Invitation {
  key: string;
  eventId: string;
  seriesId: string | null;
  title: string;
  start: IsoDateTime;
  end: IsoDateTime;
  isAllDay: boolean;
  isRecurring: boolean;
  recurrence: Recurrence | null;
  location: string | null;
  roomName: string | null;
  calendar: EventCalendarRef;
  organizer: UserRef;
  attendeeCount: number;
  invitedAt: IsoDateTime;
}

export interface BusySlot {
  start: IsoDateTime;
  end: IsoDateTime;
  isAllDay: boolean;
  availability: EventAvailability;
  key: string | null;
  title: string | null;
}

/** Kinds of absence on the company's absence chart (identity module). */
export type AbsenceKind = 'vacation' | 'sick' | 'businessTrip' | 'other';

/**
 * Days a colleague is away (vacation, sick leave, business trip, other). Whole days in the EMPLOYEE's time zone:
 * `start` is the instant their first day begins, `end` the instant the day after the last one begins (exclusive).
 * All-day events compare the dates `from`/`to` (inclusive) instead of the instants.
 */
export interface AbsenceSlot {
  id: string;
  type: AbsenceKind;
  from: IsoDate;
  to: IsoDate;
  start: IsoDateTime;
  end: IsoDateTime;
  timeZone: string;
}

export interface UserFreeBusy {
  userId: string;
  busy: BusySlot[];
  absences: AbsenceSlot[];
}

export interface RoomAvailability {
  roomId: string;
  busy: BusySlot[];
}

export interface FeedInfo {
  path: string | null;
  createdAt: IsoDateTime | null;
  lastUsedAt: IsoDateTime | null;
}

/** The event form as the API takes it (create and edit). */
export interface EventInput {
  calendarId: string | null;
  title: string;
  descriptionHtml: string | null;
  location: string | null;
  roomId: string | null;
  start: string;
  end: string;
  isAllDay: boolean;
  timeZone: string;
  recurrence: Recurrence | null;
  attendeeIds: string[];
  reminders: number[] | null;
  privacy: EventPrivacy;
  availability: EventAvailability;
  color: string | null;
}

export interface UpdateEventInput extends EventInput {
  scope: EditScope;
  occurrence: IsoDateTime | null;
  version?: number;
}

export interface RescheduleInput {
  start: string;
  end: string;
  isAllDay: boolean;
  scope: EditScope;
  occurrence: IsoDateTime | null;
}

export interface RoomInput {
  name: string;
  location: string | null;
  capacity: number;
  description: string | null;
  equipment: string[];
  color: string | null;
}

export interface RangeParams {
  from: string;
  to: string;
  calendarIds: string[];
  roomIds: string[];
}

export const calendarKeys = {
  all: ['calendar'] as const,
  list: ['calendar', 'calendars'] as const,
  calendar: (id: string) => ['calendar', 'calendar', id] as const,
  userCalendar: (userId: string) => ['calendar', 'user-calendar', userId] as const,
  groupCalendar: (groupId: string) => ['calendar', 'group-calendar', groupId] as const,
  ranges: ['calendar', 'events'] as const,
  range: (params: RangeParams) => ['calendar', 'events', params] as const,
  events: ['calendar', 'event'] as const,
  event: (key: string) => ['calendar', 'event', key] as const,
  invitations: ['calendar', 'invitations'] as const,
  counters: ['calendar', 'counters'] as const,
  freeBusy: ['calendar', 'free-busy'] as const,
  freeBusyFor: (userIds: string[], from: string, to: string, exclude?: string | null) =>
    ['calendar', 'free-busy', userIds, from, to, exclude ?? null] as const,
  rooms: ['calendar', 'rooms'] as const,
  roomAvailability: (from: string, to: string, exclude?: string | null) =>
    ['calendar', 'rooms', 'availability', from, to, exclude ?? null] as const,
  feed: ['calendar', 'feed'] as const,
};

const enc = encodeURIComponent;

/** Splits a panel key `id` / `id~yyyyMMddTHHmmssZ` into the event id and the occurrence start. */
export function parseEventKey(key: string): { id: string; occurrence: string | null } {
  const [id = '', occurrence] = key.split('~');
  return { id, occurrence: occurrence ?? null };
}

/** `2026-10-19T07:00:00Z` → `20261019T070000Z` (the occurrence part of panel keys). */
export function occurrenceToken(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(
    d.getUTCMinutes(),
  )}${pad(d.getUTCSeconds())}Z`;
}

/** `20261019T070000Z` → ISO string. */
export function occurrenceFromToken(token: string): string | null {
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/u.exec(token);
  if (!match) return null;
  return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}Z`;
}

export const calendarApi = {
  calendars: (signal?: AbortSignal) => api.get<CalendarList>('/calendar/calendars', { signal }),
  calendar: (id: string, signal?: AbortSignal) =>
    api.get<CalendarItem>(`/calendar/calendars/${enc(id)}`, { signal }),
  userCalendar: (userId: string, signal?: AbortSignal) =>
    api.get<CalendarItem>(`/calendar/users/${enc(userId)}/calendar`, { signal }),
  groupCalendar: (groupId: string, signal?: AbortSignal) =>
    api.get<CalendarItem>(`/calendar/groups/${enc(groupId)}/calendar`, { signal }),
  follow: (target: { userId?: string; groupId?: string }) =>
    api.post<CalendarItem>('/calendar/calendars/follow', target),
  unfollow: (id: string) => api.delete(`/calendar/calendars/${enc(id)}/follow`),
  calendarPreferences: (id: string, prefs: { color?: string; isVisible?: boolean }) =>
    api.put<CalendarItem>(`/calendar/calendars/${enc(id)}/preferences`, prefs),
  roomPreferences: (id: string, prefs: { color?: string; isVisible?: boolean }) =>
    api.put<Room>(`/calendar/rooms/${enc(id)}/preferences`, prefs),
  settings: (signal?: AbortSignal) => api.get<CalendarSettings>('/calendar/settings', { signal }),
  updateSettings: (settings: CalendarSettings) => api.put<CalendarSettings>('/calendar/settings', settings),

  range: (params: RangeParams, signal?: AbortSignal) =>
    api.get<OccurrenceRange>('/calendar/events', {
      query: {
        from: params.from,
        to: params.to,
        calendarIds: params.calendarIds.join(','),
        roomIds: params.roomIds.join(','),
      },
      signal,
    }),
  event: (key: string, signal?: AbortSignal) => {
    const { id, occurrence } = parseEventKey(key);
    return api.get<EventDetails>(`/calendar/events/${enc(id)}`, { query: { occurrence }, signal });
  },
  create: (input: EventInput) => api.post<EventDetails>('/calendar/events', input),
  update: (id: string, input: UpdateEventInput) =>
    api.put<EventDetails>(`/calendar/events/${enc(id)}`, input),
  reschedule: (id: string, input: RescheduleInput) =>
    api.post<EventDetails>(`/calendar/events/${enc(id)}/reschedule`, input),
  remove: (id: string, scope: EditScope, occurrence: string | null) =>
    api.delete(`/calendar/events/${enc(id)}`, {
      query: { scope, occurrence: occurrence ? occurrenceToken(occurrence) : null },
    }),
  respond: (
    id: string,
    response: Exclude<AttendeeResponse, 'pending'>,
    scope: 'all' | 'this' = 'all',
    occurrence: string | null = null,
  ) => api.post<EventDetails>(`/calendar/events/${enc(id)}/respond`, { response, scope, occurrence }),
  setReminders: (id: string, minutes: number[]) =>
    api.put<EventDetails>(`/calendar/events/${enc(id)}/reminders`, { minutes }),
  ics: (id: string) => api.get<string>(`/calendar/events/${enc(id)}/ics`),

  invitations: (signal?: AbortSignal) => api.get<Invitation[]>('/calendar/invitations', { signal }),
  counters: (signal?: AbortSignal) => api.get<{ invitations: number }>('/calendar/me/counters', { signal }),
  freeBusy: (
    userIds: string[],
    from: string,
    to: string,
    excludeEventId?: string | null,
    signal?: AbortSignal,
  ) =>
    api.get<UserFreeBusy[]>('/calendar/free-busy', {
      query: { userIds: userIds.join(','), from, to, excludeEventId },
      signal,
    }),

  rooms: (signal?: AbortSignal) => api.get<Room[]>('/calendar/rooms', { signal }),
  roomAvailability: (from: string, to: string, excludeEventId?: string | null, signal?: AbortSignal) =>
    api.get<RoomAvailability[]>('/calendar/rooms/availability', {
      query: { from, to, excludeEventId },
      signal,
    }),
  createRoom: (input: RoomInput) => api.post<Room>('/calendar/rooms', input),
  updateRoom: (id: string, input: RoomInput) => api.put<Room>(`/calendar/rooms/${enc(id)}`, input),
  deleteRoom: (id: string) => api.delete(`/calendar/rooms/${enc(id)}`),

  feed: (signal?: AbortSignal) => api.get<FeedInfo>('/calendar/feed', { signal }),
  regenerateFeed: () => api.post<FeedInfo>('/calendar/feed'),
  revokeFeed: () => api.delete('/calendar/feed'),
};

// ---- queries ------------------------------------------------------------------------------------

export function useCalendarList(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: calendarKeys.list,
    queryFn: ({ signal }) => calendarApi.calendars(signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useOccurrences(params: RangeParams | null) {
  const effective = params ?? { from: '', to: '', calendarIds: [], roomIds: [] };
  return useQuery({
    queryKey: calendarKeys.range(effective),
    queryFn: ({ signal }) => calendarApi.range(effective, signal),
    enabled: !!params && (params.calendarIds.length > 0 || params.roomIds.length > 0),
    placeholderData: keepPreviousData,
  });
}

export function useEventDetails(key: string | null) {
  return useQuery({
    queryKey: calendarKeys.event(key ?? ''),
    queryFn: ({ signal }) => calendarApi.event(key ?? '', signal),
    enabled: !!key,
  });
}

export function useInvitations() {
  return useQuery({
    queryKey: calendarKeys.invitations,
    queryFn: ({ signal }) => calendarApi.invitations(signal),
  });
}

export function useCalendarCounters(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: calendarKeys.counters,
    queryFn: ({ signal }) => calendarApi.counters(signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useFreeBusy(
  userIds: string[],
  from: string | null,
  to: string | null,
  excludeEventId?: string | null,
) {
  const ids = [...userIds].sort();
  return useQuery({
    queryKey: calendarKeys.freeBusyFor(ids, from ?? '', to ?? '', excludeEventId),
    queryFn: ({ signal }) => calendarApi.freeBusy(ids, from ?? '', to ?? '', excludeEventId, signal),
    enabled: ids.length > 0 && !!from && !!to,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useRooms() {
  return useQuery({
    queryKey: calendarKeys.rooms,
    queryFn: ({ signal }) => calendarApi.rooms(signal),
    staleTime: 60_000,
  });
}

export function useRoomAvailability(from: string | null, to: string | null, excludeEventId?: string | null) {
  return useQuery({
    queryKey: calendarKeys.roomAvailability(from ?? '', to ?? '', excludeEventId),
    queryFn: ({ signal }) => calendarApi.roomAvailability(from ?? '', to ?? '', excludeEventId, signal),
    enabled: !!from && !!to,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useUserCalendar(userId: string | null) {
  return useQuery({
    queryKey: calendarKeys.userCalendar(userId ?? ''),
    queryFn: ({ signal }) => calendarApi.userCalendar(userId ?? '', signal),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });
}

export function useGroupCalendar(groupId: string) {
  return useQuery({
    queryKey: calendarKeys.groupCalendar(groupId),
    queryFn: ({ signal }) => calendarApi.groupCalendar(groupId, signal),
    retry: false,
  });
}

export function useFeed(enabled: boolean) {
  return useQuery({
    queryKey: calendarKeys.feed,
    queryFn: ({ signal }) => calendarApi.feed(signal),
    enabled,
  });
}

// ---- cache helpers -----------------------------------------------------------------------------------

/** Everything an event change can affect: ranges, cards, invitations, counters, free/busy and room availability. */
export async function invalidateEvents(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: calendarKeys.ranges }),
    invalidateEventCards(queryClient),
    queryClient.invalidateQueries({ queryKey: calendarKeys.invitations }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.counters }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.freeBusy }),
    queryClient.invalidateQueries({ queryKey: [...calendarKeys.rooms, 'availability'] }),
    queryClient.invalidateQueries({ queryKey: calendarKeys.list }),
  ]);
}

/** Cards of events this tab deleted: never refetched (they would answer 404 while their panel closes). */
const deletedCards = new Set<string>();

/** Remembers that the card `key` was deleted here (its panel is closing). */
export function forgetEventCard(key: string): void {
  deletedCards.add(key);
}

/** Refreshes the open event cards (except the ones deleted in this tab). */
export function invalidateEventCards(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: calendarKeys.events,
    predicate: (query) => !deletedCards.has(String(query.queryKey[2])),
  });
}

/** Patches the calendar list cache (optimistic visibility and color changes); returns the rollback. */
export function patchCalendarList(
  queryClient: QueryClient,
  patch: (list: CalendarList) => CalendarList,
): () => void {
  const previous = queryClient.getQueryData<CalendarList>(calendarKeys.list);
  if (previous) queryClient.setQueryData(calendarKeys.list, patch(previous));
  return () => {
    if (previous) queryClient.setQueryData(calendarKeys.list, previous);
  };
}

/** A mutation that refreshes event-related queries afterwards. */
export function useEventMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
  options: { successMessage?: string; silent?: boolean } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    meta: { successMessage: options.successMessage, silent: options.silent },
    onSuccess: (result) => {
      if (isEventDetails(result)) queryClient.setQueryData(calendarKeys.event(result.key), result);
    },
    onSettled: () => invalidateEvents(queryClient),
  });
}

export function isEventDetails(value: unknown): value is EventDetails {
  return typeof value === 'object' && value !== null && 'attendees' in value && 'permissions' in value;
}
